import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  Tooltip,
  Line,
  LineChart,
} from "recharts";
import { TrendingUp, TrendingDown, BarChart3, AreaChart as AreaChartIcon } from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";

const StatBox = ({
  title,
  subtitle,
  icon,
  chartData = [],
  chartType = "area",
  className = "",
  onClick,
  timeSeriesData = [],
  dataKey = "value",
  titleTooltip,
}) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isHovered, setIsHovered] = useState(false);
  const [processedChartData, setProcessedChartData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [activeChartType, setActiveChartType] = useState(chartType);
  const { gradient, theme } = useSidebarContext();

  const toggleChartType = useCallback((e) => {
    e.stopPropagation();
    setActiveChartType((prev) => (prev === "area" ? "bar" : "area"));
  }, []);

  const formatTitle = (value) => {
    if (typeof value === "number") {
      return new Intl.NumberFormat("en-PK").format(value);
    }
    return value;
  };

  useEffect(() => {
    if (!Array.isArray(timeSeriesData) || timeSeriesData.length === 0) {
      setProcessedChartData([]);
      setAvailableYears([new Date().getFullYear()]);
      return;
    }

    const years = [
      ...new Set(
        timeSeriesData
          .map((item) => {
            if (!item.date) return null;
            const year = parseInt(item.date.split("-")[0]);
            return isNaN(year) ? null : year;
          })
          .filter((year) => year !== null)
      ),
    ].sort((a, b) => a - b);

    setAvailableYears(years.length > 0 ? years : [new Date().getFullYear()]);

    const currentYear = selectedYear;
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const monthlyData = {};
    const yearData = timeSeriesData.filter((item) => item.date && parseInt(item.date.split("-")[0]) === currentYear);

    yearData.forEach((item) => {
      const month = parseInt(item.date.split("-")[1]) - 1;
      if (month >= 0 && month <= 11) {
        if (!monthlyData[month]) {
          monthlyData[month] = {
            income: 0, totalPlotIncome: 0, expense: 0, netProfit: 0,
            totalAvailablePlots: 0, totalBookedPlots: 0, totalCustomers: 0,
            totalReceivableAmount: 0, totalAvailableMarla: 0, totalBookedMarla: 0,
            totalMarla: 0, totalOverdueAmount: 0, cashFlowThisMonth: 0,
            monthlyCollectionRate: 0, count: 0
          };
        }
        monthlyData[month].income += item.income || 0;
        monthlyData[month].totalPlotIncome += item.totalPlotIncome || 0;
        monthlyData[month].expense += item.expense || 0;
        monthlyData[month].netProfit = monthlyData[month].income - monthlyData[month].expense;
        monthlyData[month].totalAvailablePlots = item.totalAvailablePlots || 0;
        monthlyData[month].totalBookedPlots = item.totalBookedPlots || 0;
        monthlyData[month].totalCustomers = item.totalCustomers || 0;
        monthlyData[month].totalReceivableAmount = item.totalReceivableAmount || 0;
        monthlyData[month].totalAvailableMarla = item.totalAvailableMarla || 0;
        monthlyData[month].totalBookedMarla = item.totalBookedMarla || 0;
        monthlyData[month].totalMarla = item.totalMarla || 0;
        monthlyData[month].totalOverdueAmount = item.totalOverdueAmount || 0;
        monthlyData[month].cashFlowThisMonth = item.cashFlowThisMonth || 0;
        monthlyData[month].monthlyCollectionRate = item.monthlyCollectionRate || 0;
        monthlyData[month].count += 1;
      }
    });

    const chartDataArray = [];
    const currentMonth = new Date().getMonth();
    const isCurrentYear = currentYear === new Date().getFullYear();

    for (let i = 0; i < 12; i++) {
      if (isCurrentYear && i > currentMonth) break;
      const monthData = monthlyData[i];
      const entry = {
        name: monthNames[i],
        value: monthData?.[dataKey] || 0,
        ...monthData
      };
      chartDataArray.push(entry);
    }
    setProcessedChartData(chartDataArray);
  }, [timeSeriesData, selectedYear, dataKey]);

  const finalChartData = processedChartData.length > 0 ? processedChartData : [{ name: "", value: 0 }];
  const gradientId = useMemo(() => `statGrad-${dataKey}-${Math.random().toString(36).substr(2, 5)}`, [dataKey]);

  // Calculate trend
  const trendData = useMemo(() => {
    if (!Array.isArray(timeSeriesData) || timeSeriesData.length < 2) {
      return { trend: 0, trendPercentage: 0, trendData: [], isPositive: true, hasData: false };
    }

    const yearData = timeSeriesData
      .filter((item) => {
        if (!item.date) return false;
        try {
          const dateStr = item.date.toString();
          const year = parseInt(dateStr.split("-")[0] || dateStr.split("/")[0] || dateStr.substring(0, 4));
          return !isNaN(year) && year === selectedYear;
        } catch { return false; }
      })
      .sort((a, b) => { try { return new Date(a.date) - new Date(b.date); } catch { return 0; } });

    let dataToUse = yearData;
    if (yearData.length < 2) {
      dataToUse = timeSeriesData
        .filter((item) => item.date)
        .sort((a, b) => { try { return new Date(a.date) - new Date(b.date); } catch { return 0; } })
        .slice(-14);
    }

    if (dataToUse.length < 2) {
      return { trend: 0, trendPercentage: 0, trendData: [], isPositive: true, hasData: false };
    }

    const values = dataToUse.map((item) => {
      const value = item[dataKey] !== undefined ? item[dataKey] : (item.value !== undefined ? item.value : 0);
      return typeof value === 'number' && !isNaN(value) ? value : 0;
    }).filter(v => v !== null && v !== undefined);

    if (values.length < 2) {
      return { trend: 0, trendPercentage: 0, trendData: [], isPositive: true, hasData: false };
    }

    const firstValue = values[0] || 0;
    const lastValue = values[values.length - 1] || 0;
    const trend = lastValue - firstValue;
    const trendPercentage = firstValue !== 0
      ? ((trend / Math.abs(firstValue)) * 100)
      : (lastValue > 0 ? 100 : 0);
    const isPositive = trend >= 0;

    const miniChartData = values.slice(-7).map((val, idx) => ({
      name: `${idx + 1}`, value: val
    }));

    return {
      trend,
      trendPercentage: Math.abs(trendPercentage),
      trendData: miniChartData,
      isPositive,
      hasData: values.length > 0 && values.some(v => v !== 0)
    };
  }, [timeSeriesData, dataKey, selectedYear]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900/95 backdrop-blur-md border border-white/10 rounded-xl p-2.5 shadow-2xl ring-1 ring-white/20">
          <p className="text-slate-400 font-bold text-[9px] uppercase tracking-widest mb-1.5 border-b border-white/5 pb-1">{label} {selectedYear}</p>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.mui.main }} />
            <span className="text-white font-black text-xs">
              {dataKey.toLowerCase().includes("income") || dataKey.toLowerCase().includes("expense") || dataKey.toLowerCase().includes("receivable") || dataKey.toLowerCase().includes("overdue") || dataKey.toLowerCase().includes("cashflow") || dataKey.toLowerCase().includes("profit") || dataKey.toLowerCase().includes("plotincome")
                ? `Rs. ${new Intl.NumberFormat("en-PK").format(payload[0].value)}`
                : new Intl.NumberFormat("en-PK").format(payload[0].value)}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  const hasChartData = finalChartData.length > 0 && finalChartData.some(item => (item.value || 0) > 0);

  // Accent color based on icon color or a default
  const accentColor = theme.mui.main || "#10b981";

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      className={`group relative ${gradient} rounded-2xl transition-all duration-500 ease-out border border-white/[0.08] shadow-lg cursor-pointer hover:shadow-2xl hover:border-white/20 hover:scale-[1.02] ${className}`}
      style={{
        minHeight: "180px",
      }}
    >
      {/* Ambient glow effect on hover */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 50% 0%, ${accentColor}08 0%, transparent 70%)`,
        }}
      />

      {/* Top accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-all duration-500"
        style={{
          background: `linear-gradient(90deg, transparent, ${accentColor}60, transparent)`,
        }}
      />

      <div className="relative p-4 sm:p-5 h-full flex flex-col">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex gap-3 items-center flex-1 min-w-0">
            {/* Icon Container */}
            <div
              className="relative p-2.5 rounded-xl border border-white/[0.08] group-hover:border-white/20 transition-all duration-500 flex-shrink-0 overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}05)`,
              }}
            >
              {/* Icon glow */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `radial-gradient(circle, ${accentColor}20 0%, transparent 70%)` }}
              />
              {React.cloneElement(icon, {
                className: `w-5 h-5 relative z-10 transition-all duration-300 group-hover:scale-110`,
                style: { color: accentColor }
              })}
            </div>

            {/* Title & Subtitle */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 group-hover:text-slate-300 transition-colors truncate">
                  {subtitle}
                </p>
                {titleTooltip && (
                  <div className="group/info relative inline-flex">
                    <span className="text-slate-500 text-[10px] cursor-help hover:text-slate-300 transition-colors select-none leading-none">ⓘ</span>
                    <div
                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-[9999] pointer-events-none
                                 opacity-0 group-hover/info:opacity-100 scale-95 group-hover/info:scale-100
                                 transition-all duration-200 ease-out"
                    >
                      <div className="px-3 py-2 bg-slate-900 border border-white/15 rounded-xl shadow-2xl
                                      text-white text-[10px] leading-relaxed text-center
                                      w-max max-w-[200px] whitespace-normal">
                        {titleTooltip}
                      </div>
                      {/* Arrow */}
                      <div className="flex justify-center -mt-[1px]">
                        <div className="border-4 border-transparent border-t-slate-900" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <h3 className="text-2xl font-black text-white tracking-tight truncate group-hover:text-white/95 transition-colors">
                {formatTitle(title)}
              </h3>
            </div>
          </div>

          {/* Chart Type Toggle Button */}
          <button
            onClick={toggleChartType}
            className="relative p-1.5 rounded-lg border border-white/[0.08] hover:border-white/25 bg-white/[0.03] hover:bg-white/[0.08] transition-all duration-300 group/toggle flex-shrink-0 ml-2"
            title={activeChartType === "area" ? "Switch to bar chart" : "Switch to area chart"}
          >
            <div className="relative w-4 h-4">
              <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${activeChartType === "bar" ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-75 rotate-90"}`}>
                <BarChart3 className="w-3.5 h-3.5 text-slate-400 group-hover/toggle:text-white transition-colors" />
              </div>
              <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${activeChartType === "area" ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-75 -rotate-90"}`}>
                <AreaChartIcon className="w-3.5 h-3.5 text-slate-400 group-hover/toggle:text-white transition-colors" />
              </div>
            </div>
          </button>
        </div>

        {/* Trend Row */}
        {trendData.hasData && trendData.trendData.length > 0 && (
          <div className="flex items-center gap-2 mb-2">
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border ${trendData.isPositive
                ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                : 'text-red-400 bg-red-500/10 border-red-500/20'
              }`}>
              {trendData.isPositive ? (
                <TrendingUp className="w-2.5 h-2.5" />
              ) : (
                <TrendingDown className="w-2.5 h-2.5" />
              )}
              <span>{trendData.trendPercentage.toFixed(1)}%</span>
            </div>
            {/* Mini trend sparkline */}
            <div className="w-16 h-5 opacity-60 group-hover:opacity-100 transition-opacity">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData.trendData}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={trendData.isPositive ? "#10b981" : "#ef4444"}
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Chart Section — fills remaining space */}
        <div className="flex-1 mt-auto -mx-2 -mb-1 min-h-[60px] relative">
          {/* Gradient overlay at the bottom of the chart */}
          <div
            className="absolute bottom-0 left-0 right-0 h-8 z-10 pointer-events-none"
            style={{
              background: `linear-gradient(to top, var(--tw-gradient-from, rgba(15,23,42,0.9)) 0%, transparent 100%)`,
            }}
          />

          {hasChartData ? (
            <ResponsiveContainer width="100%" height="100%">
              {activeChartType === "area" ? (
                <AreaChart data={finalChartData}>
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={accentColor} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={accentColor} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <Tooltip content={<CustomTooltip />} cursor={false} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={accentColor}
                    strokeWidth={2}
                    fill={`url(#${gradientId})`}
                    dot={false}
                    activeDot={{ r: 3, strokeWidth: 0, fill: accentColor }}
                  />
                </AreaChart>
              ) : (
                <BarChart data={finalChartData}>
                  <defs>
                    <linearGradient id={`${gradientId}-bar`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={accentColor} stopOpacity={0.8} />
                      <stop offset="100%" stopColor={accentColor} stopOpacity={0.15} />
                    </linearGradient>
                  </defs>
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                  <Bar
                    dataKey="value"
                    fill={`url(#${gradientId}-bar)`}
                    radius={[4, 4, 0, 0]}
                    barSize={12}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          ) : (
            /* Empty state — subtle pattern */
            <div className="w-full h-full flex items-center justify-center opacity-30">
              <div className="flex gap-1 items-end h-8">
                {[3, 5, 4, 7, 6, 8, 5, 7, 4, 6, 3, 5].map((h, i) => (
                  <div
                    key={i}
                    className="w-1.5 rounded-t-sm bg-white/10"
                    style={{ height: `${h * 3}px` }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatBox;
